import { useState, useEffect, useCallback, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface WordCyclerProps {
  /** Words to cycle through */
  words: string[];
  /** Milliseconds each word stays visible before being deleted (default 2400) */
  holdTime?: number;
  /** Milliseconds per character when typing (default 55) */
  typeSpeed?: number;
  /** Milliseconds per character when deleting (default 35) */
  deleteSpeed?: number;
  /** Optional className applied to the cycling word span */
  className?: string;
  /** Cursor color class (default 'bg-fwd-yellow') */
  cursorClass?: string;
  /** Show blinking cursor (default true) */
  cursor?: boolean;
  /**
   * If provided, the component mounts with this word already displayed and
   * deletes it before starting the normal cycle. Useful when a Typewriter has
   * already typed the initial word and you want the cursor to "go back" and
   * start swapping.
   */
  initialWord?: string;
  /** Delay in ms before the first deletion begins (default 800) */
  startDelay?: number;
}

type Phase = "waiting" | "typing" | "holding" | "deleting";

/**
 * Cycles through a list of words with a character-by-character type and
 * delete animation — like someone correcting a word in a text editor.
 *
 * Usage:
 * ```tsx
 * <WordCycler words={['Year', 'Quarter', 'Month', 'Campaign']} className="text-fwd-yellow" />
 * ```
 */
export const WordCycler = ({
  words,
  holdTime = 2400,
  typeSpeed = 55,
  deleteSpeed = 35,
  className = "",
  cursorClass = "bg-fwd-yellow",
  cursor = true,
  initialWord,
  startDelay = 800,
}: WordCyclerProps) => {
  const reducedMotion = useReducedMotion();
  const [wordIndex, setWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState(initialWord ?? words[0] ?? "");
  const [phase, setPhase] = useState<Phase>(initialWord ? "waiting" : "typing");
  const timeoutRef = useRef<number>(undefined);

  // Guard against an empty words array so currentWord.length below never throws.
  const hasWords = words.length > 0;
  const currentWord = hasWords ? words[wordIndex % words.length] : "";

  const jitter = useCallback(
    (base: number) => base + Math.random() * base * 0.5,
    [],
  );

  useEffect(() => {
    clearTimeout(timeoutRef.current);

    if (!hasWords || reducedMotion) return;

    if (phase === "waiting") {
      // Pause before starting to delete the initial word
      timeoutRef.current = window.setTimeout(() => {
        setPhase("deleting");
      }, startDelay);
    } else if (phase === "typing") {
      if (displayText.length < currentWord.length) {
        timeoutRef.current = window.setTimeout(() => {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        }, jitter(typeSpeed));
      } else {
        // Done typing — hold
        setPhase("holding");
      }
    } else if (phase === "holding") {
      timeoutRef.current = window.setTimeout(() => {
        setPhase("deleting");
      }, holdTime);
    } else if (phase === "deleting") {
      if (displayText.length > 0) {
        timeoutRef.current = window.setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, jitter(deleteSpeed));
      } else {
        // Done deleting — advance to next word
        setWordIndex((prev) => (prev + 1) % words.length);
        setPhase("typing");
      }
    }

    return () => clearTimeout(timeoutRef.current);
  }, [
    displayText,
    phase,
    currentWord,
    words.length,
    hasWords,
    holdTime,
    typeSpeed,
    deleteSpeed,
    jitter,
    startDelay,
    reducedMotion,
  ]);

  return (
    <span className={`inline ${className}`}>
      {displayText}
      {cursor && (
        <motion.span
          animate={reducedMotion ? undefined : { opacity: [1, 0] }}
          transition={
            reducedMotion
              ? undefined
              : { duration: 0.5, repeat: Infinity, repeatType: "reverse" }
          }
          className={`inline-block w-[3px] h-[0.85em] ${cursorClass} ml-[1px] align-baseline translate-y-[0.1em]`}
        />
      )}
    </span>
  );
};
